import React from 'react';
import { Link } from 'react-router-dom';

// In-app copy of the Terms of Use / EULA. Mirrors app/app/(auth)/terms.jsx so
// the web flow satisfies the same App Store guideline 1.2 requirement (every
// account must explicitly accept these terms before posting). Linked from the
// Register page checkbox, the Login terms blurb, and Settings → Terms of Use.
const Sec = ({ h, children }) => (
  <section style={s.sec}>
    <h2 style={s.h2}>{h}</h2>
    {children}
  </section>
);

const Li = ({ children }) => <li style={s.li}>{children}</li>;

const Terms = () => (
  <div style={s.page}>
    <div style={s.container}>
      <nav style={s.nav}>
        <Link to="/" style={s.brand}>MyCarCollection</Link>
      </nav>

      <header style={s.header}>
        <p style={s.muted}>Last updated: 13 August 2026</p>
        <h1 style={s.h1}>MyCarCollection — End User License Agreement</h1>
      </header>

      <Sec h="1. Acceptance of these Terms">
        <p style={s.p}>
          By creating an account, signing in, or otherwise using MyCarCollection (the “Service”)
          you agree to be bound by these Terms of Use. If you do not agree, you must not use
          the Service. We may update these Terms from time to time; continued use after a
          change is published constitutes acceptance of the updated Terms.
        </p>
      </Sec>

      <Sec h="2. Zero tolerance for objectionable content and abusive users">
        <p style={s.p}>
          MyCarCollection has zero tolerance for objectionable content and abusive behaviour.
          You agree that you will not post, upload, transmit, share, or otherwise make
          available any content that is, or that a reasonable person would consider to be:
        </p>
        <ul style={s.ul}>
          <Li>unlawful, threatening, defamatory, obscene, or pornographic;</Li>
          <Li>harassing, bullying, abusive, hateful, or discriminatory toward any individual or group;</Li>
          <Li>depicting or encouraging violence, self-harm, animal cruelty, or sexual exploitation;</Li>
          <Li>promoting illegal activity, including the sale of stolen vehicles or parts;</Li>
          <Li>infringing the intellectual property, privacy, or other rights of any person;</Li>
          <Li>impersonating another person or misrepresenting your affiliation with anyone;</Li>
          <Li>spam, unsolicited advertising, scams, or repetitive low-quality content.</Li>
        </ul>
        <p style={s.p}>
          We reserve the right to remove any content, and to suspend or permanently terminate
          any account, that violates these rules, in our sole discretion and without notice.
          Repeated or severe violations will result in a permanent ban.
        </p>
      </Sec>

      <Sec h="3. Reporting and blocking">
        <p style={s.p}>
          Every post, comment, vehicle entry, review, message, event, and user profile in the
          Service can be reported by tapping the menu (•••) on the item and selecting
          “Report.” You can block any other user from their profile or from any of their
          content; once blocked, that user’s content disappears from your feed immediately
          and they can no longer interact with you. Reports are reviewed by our moderation
          team and actioned within 24 hours.
        </p>
      </Sec>

      <Sec h="4. Your responsibilities">
        <p style={s.p}>
          You are solely responsible for the content you submit and the interactions you have
          with other users. You agree to provide accurate information when creating your
          account and to keep your password secure. You must be at least 13 years old to use
          the Service.
        </p>
      </Sec>

      <Sec h="5. Account suspension and termination">
        <p style={s.p}>
          We may suspend or terminate your account at any time for any breach of these Terms,
          including, without limitation, the posting of objectionable content or abusive
          behaviour toward other users. You may delete your account at any time from
          Settings → Account.
        </p>
      </Sec>

      <Sec h="6. Support subscriptions">
        <p style={s.p}>
          MyCarCollection is free to use. “Support the App” offers optional auto-renewable
          monthly subscriptions — Supporter, Enthusiast, and Collector — that help cover the
          running costs of the Service. They unlock supporter recognition only (a place on the
          Supporters Wall in the mobile app); they do not gate any feature, and nothing is
          withheld from users who do not subscribe.
        </p>
        <p style={s.p}>
          Subscriptions are purchased and managed only in the MyCarCollection mobile app,
          through Apple or Google. They are not sold on this website, and the Supporters Wall
          is not shown here.
        </p>
        <ul style={s.ul}>
          <Li>
            Each tier bills monthly. The price for your region, in your local currency, is
            shown on the Support the App screen before you confirm the purchase.
          </Li>
          <Li>
            Payment is charged to your Apple ID (or Google Play) account on confirmation of
            purchase.
          </Li>
          <Li>
            A subscription renews automatically for the same period and price unless
            auto-renewal is turned off at least 24 hours before the end of the current period.
            Your account is charged for the renewal within the 24 hours before the current
            period ends.
          </Li>
          <Li>
            You can manage your subscription and turn off auto-renewal at any time after
            purchase, in your Apple ID Account Settings or in Google Play. Cancelling stops
            the next renewal; it does not refund the period already paid for, and your
            supporter status runs to the end of that period.
          </Li>
          <Li>
            Moving to a cheaper tier takes effect at the end of the current billing period —
            you are not charged twice for the same month.
          </Li>
          <Li>
            Refunds are handled by Apple or Google under their own terms of sale, not by us.
            Where a free trial is offered, any unused portion of it is forfeited when you
            purchase a subscription.
          </Li>
          <Li>
            A subscription bought on one device can be restored on another with “Restore
            Purchases” on the Support the App screen.
          </Li>
        </ul>
        <p style={s.p}>
          Prices and tiers may change; any change applies from your next billing period and
          never to a period you have already paid for.
        </p>
      </Sec>

      <Sec h="7. Disclaimer of warranties">
        <p style={s.p}>
          The Service is provided on an “as is” and “as available” basis without warranties
          of any kind, express or implied. We do not guarantee uninterrupted or error-free
          operation of the Service or the accuracy of any content posted by other users.
        </p>
      </Sec>

      <Sec h="8. Limitation of liability">
        <p style={s.p}>
          To the maximum extent permitted by law, MyCarCollection and its operators shall
          not be liable for any indirect, incidental, special, consequential, or punitive
          damages arising out of or related to your use of the Service.
        </p>
      </Sec>

      <Sec h="9. Contact">
        <p style={s.p}>
          Questions, complaints, or notice of objectionable content can be sent to{' '}
          <a href="mailto:support@mycarcollection.app" style={s.link}>
            support@mycarcollection.app
          </a>
          . We respond to reports within 24 hours.
        </p>
      </Sec>

      <p style={s.footer}>
        By clicking “Create account”, “Continue with Apple”, “Continue with Google”, or
        “Continue with Discord”, you confirm that you have read and agree to these Terms of
        Use.
      </p>
    </div>
  </div>
);

const s = {
  page: { minHeight: '100vh', color: '#e6e9f0', paddingBottom: 80 },
  container: { maxWidth: 760, margin: '0 auto', padding: '0 24px' },
  nav: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '24px 0', marginBottom: 24,
  },
  brand: {
    color: '#fff', fontSize: '1.05rem', fontWeight: 800, letterSpacing: -0.4,
    textDecoration: 'none',
  },
  header: { padding: '24px 0 8px' },
  muted: { color: '#5a657a', fontSize: '0.8rem', marginBottom: 8 },
  h1: { fontSize: '1.9rem', fontWeight: 800, letterSpacing: -0.6, lineHeight: 1.15, marginBottom: 8 },
  sec: { marginTop: 28 },
  h2: { fontSize: '1rem', fontWeight: 700, marginBottom: 8, color: '#fff' },
  p: { color: '#c8cfe0', fontSize: '0.92rem', lineHeight: 1.65, marginBottom: 10 },
  ul: { paddingLeft: 22, margin: '4px 0 10px' },
  li: { color: '#c8cfe0', fontSize: '0.92rem', lineHeight: 1.6, marginBottom: 4 },
  link: { color: '#4a9eff', textDecoration: 'underline' },
  footer: {
    marginTop: 36, fontStyle: 'italic',
    color: '#8892b0', fontSize: '0.85rem', lineHeight: 1.6,
  },
};

export default Terms;
